// Phase 25 — Shared collective-case segment module (single source of truth).
//
// The Phase-22 contact-list builder defined its member-segment predicates
// (docs-missing, payment-due, status ternary) inline. Phase 25 extracts them
// here so BOTH the contact-list modal AND the new bulk-mailer consume ONE
// definition of "behind on payment" and "docs missing" — no duplicated
// predicate. A docs-IN-ORDER axis (the inverse of docs-missing) is added as a
// targetable segment of its own.
//
// Authored as ESM `.mjs` so it loads unchanged in BOTH the Vite frontend and a
// plain `node` smoke harness (a `.mjs` is always ESM regardless of the nearest
// package.json `type`), keeping segment resolution testable without a browser.
//
// Pure: no React, no DOM, no network. A member row is { link, client, status }
// where:
//   link.payments : [{ roundNumber, amount, ... }]
//   client.id / client.quantum / client.email / address fields
//   status        : link.status mirror — 'Prospect' | 'Active' | 'Suspended'
//                   | 'Terminated' | 'Archived' | undefined (absent → treated
//                   as null, NOT Terminated)

// 141 §1 — the five-value lifecycle (§0-bis ruling): Prospect → Active →
// Suspended → Terminated → Archived, in that order — consumers that map this
// array (the segment ticks, the roster picker) render lifecycle order for
// free. A value must NEVER be producible anywhere before it is known here:
// knownStatus() resolves an unknown value to null, and null matches EVERY
// segment (see `matches`), including a secretary's saved bulk mailings.
export const KNOWN_STATUSES = [
  "Prospect",
  "Active",
  "Suspended",
  "Terminated",
  "Archived",
];

export function knownStatus(v) {
  return KNOWN_STATUSES.includes(v) ? v : null; // absent → null (NOT Terminated)
}

// Phase 43 CORRECTION (item 3) — a lifecycle date slot PER status value
// (retained, not one rolling date), so a membership carries dates the way a
// regular case does. 141 §1 added the two new values' slots: Prospect's is the
// day the coordinates arrived, Archived's mirrors the matter's own.
//
// 141 §2.1 — IT LIVES HERE BECAUSE IT IS NOW READ BY FOUR PLACES AND IT IS
// PART OF THE VOCABULARY. It stood in three: the CCV roster's closure (five
// slots), `clientMatters.mjs` (three — a twin that never learned the two new
// values, and it is what the client record ranks "most recent" by), and, from
// §2.1, `CaseClient`'s own columns, which the server route stamps. A map of
// which column records a value is not a fact about collective segments or
// about one view; it is a fact about the value, and `KNOWN_STATUSES` is where
// facts about the value live. The column names are the SAME on both models by
// design (the migration's own note), so ONE map answers for both kinds of
// matter.
export const LINK_STATUS_DATE = {
  Prospect: "prospectDate",
  Active: "joinedDate",
  Suspended: "suspendedDate",
  Terminated: "terminatedDate",
  Archived: "archivedDate",
};

/** The date column recording when a link ENTERED its current status, or null
 *  for a link with no status / a status outside the vocabulary. */
export function linkStatusDateCol(status) {
  return LINK_STATUS_DATE[status] || null;
}

export function hasEmail(c) {
  return !!(c && typeof c.email === "string" && c.email.trim().includes("@"));
}

// Phase 25.1 (D5) — lenient address-FORMAT check. A grossly malformed address
// (no @, no dot-in-domain, whitespace) is what aborts a whole Graph draft, so
// the bulk-mailer treats such addresses as un-sendable (skipped, mirroring the
// no-email skip) rather than carrying them into the recipient set. This MIRRORS
// the server-side validator in server/src/routes/bulkMailings.js — kept in sync
// by hand because that CommonJS module cannot import this ESM file. It is NOT an
// RFC-5322 validator; it only catches the garbage that breaks the draft.
const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isEmailFormatValid(email) {
  return typeof email === "string" && EMAIL_FORMAT_RE.test(email.trim());
}

// Convenience: a client has a SENDABLE email (present AND well-formed).
export function hasSendableEmail(c) {
  return hasEmail(c) && isEmailFormatValid(c.email);
}

// Addressee name for paper letters. Company wins; else title + names; else
// fall back to the app's pname() helper, then a stable "Client #id".
export function addresseeName(c, pname) {
  if (c.companyName && c.companyName.trim()) return c.companyName.trim();
  const parts = [c.title, c.firstName, c.lastName]
    .map((x) => (x || "").trim())
    .filter(Boolean);
  if (parts.length) return parts.join(" ");
  return (pname && pname(c)) || `Client #${c.id}`;
}

export function chunk(arr, size) {
  const out = [];
  const n = Math.max(1, size | 0);
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

// Index CCDocumentStatus rows by `${clientId}:${documentTypeId}` for O(1)
// per-member lookups.
export function buildDocStatusIndex(docStatuses) {
  const m = new Map();
  for (const s of docStatuses || []) {
    m.set(`${s.clientId}:${s.documentTypeId}`, s);
  }
  return m;
}

// Build a bound evaluator over a fixed context (the loaded doc-tracking types +
// statuses, the retainer rounds, and the server-computed member money). Both
// the contact-list modal and the bulk-mailer construct one of these and share
// the identical predicate + matching semantics.
//
// C.2 — the round arithmetic (calcRound over rounds + payment sums) moved
// server-side to server/src/lib/retainerRounds.js behind
// POST /api/retainer/compute (recording-proven bit-exact:
// recordings/c2-rounds/). The evaluator no longer computes money: it is
// handed each member's outstanding balance, already cents-rounded and
// clamped by the SAME arithmetic this file used to run (the server preserves
// it verbatim, including the accumulation order).
//
// ctx:
//   docTypes           : [{ id, ... }]
//   docStatusIndex     : Map from buildDocStatusIndex()  (or docStatuses array)
//   rounds             : [{ roundNumber, formula }]  (axis availability only)
//   amountDueByClientId: { [clientId]: number } — perMember.amountDue from
//                        POST /api/retainer/compute
export function makeSegmentEvaluator(ctx = {}) {
  const docTypes = ctx.docTypes || [];
  const docStatusIndex =
    ctx.docStatusIndex instanceof Map
      ? ctx.docStatusIndex
      : buildDocStatusIndex(ctx.docStatuses || []);
  const rounds = ctx.rounds || [];
  const amountDueByClientId = ctx.amountDueByClientId || null;

  const hasDocTypes = docTypes.length > 0; // axis availability (docs)
  const hasRounds = rounds.length > 0; // axis availability (payment)

  // Count received document types for a member.
  const receivedCount = (m) => {
    let received = 0;
    for (const ty of docTypes) {
      const s = docStatusIndex.get(`${m.client.id}:${ty.id}`);
      if (s && s.received) received++;
    }
    return received;
  };

  // Missing at least one requested document.
  const docMissing = (m) => {
    if (!hasDocTypes) return false;
    return receivedCount(m) < docTypes.length;
  };

  // Phase 25 — docs-in-order: the exact inverse of docMissing. Every requested
  // document type received. Only meaningful when there ARE requested types.
  const docsInOrder = (m) => {
    if (!hasDocTypes) return false;
    return receivedCount(m) === docTypes.length;
  };

  // Outstanding balance across all rounds (fee rounds − late payments).
  // Numeric, clamped at 0 and rounded to cents — SERVER-COMPUTED (C.2): the
  // exact loop that lived here runs verbatim in
  // server/src/lib/retainerRounds.js and hands back perMember.amountDue.
  // The {amountDue} merge token (Phase-25 RESHAPE) is frozen per recipient at
  // fire-time from THIS value, so the dunning amount in an email always
  // equals what the segment UI shows.
  const amountDue = (m) => {
    if (!hasRounds) return 0;
    const v = amountDueByClientId ? amountDueByClientId[m.client.id] : undefined;
    return v == null ? 0 : v;
  };

  // Any outstanding balance across all rounds (behind on payment).
  const paymentDue = (m) => {
    if (!hasRounds) return false;
    return amountDue(m) > 0.005; // 0.5-cent tolerance
  };

  // Combined matcher. `filt` carries the toggles:
  //   combine        : 'AND' | 'OR'
  //   useDocs        : require docMissing
  //   useDocsInOrder : require docsInOrder
  //   usePay         : require paymentDue
  //   useStatus      : restrict to ticked statuses
  //   statusSel      : { Prospect, Active, Suspended, Terminated, Archived }
  //                    booleans
  //
  // Hard status guard: a Terminated member is excluded unless the user
  // explicitly ticked Terminated — independent of axes / AND-OR mode. With no
  // axis enabled, every non-excluded member matches (identical to Phase 22).
  // 141 §1 — Archived joins the hard guard, same shape: an archived member is
  // further gone than a terminated one, and an OR-mode axis match must not
  // pull either into a mailing nobody aimed at them.
  // 141 correction [RULED Laurent 2026-08-27] — Prospect joins too, and the
  // reason is OR mode: the status tick is ONE AXIS AMONG SEVERAL, so any
  // other matching axis overrides it — a saved "documents missing OR payment
  // due" segment would otherwise reach an enquirer who is missing documents
  // because they arrived yesterday. Each of the three reads its OWN tick, so
  // a filter saved before its value existed (no key in statusSel) keeps
  // excluding, and a FRESH segment still includes prospects because
  // defaultSegmentFilter ticks Prospect on — the guard only bites when the
  // tick is off, which is exactly the case for every segment saved before
  // this vocabulary existed.
  const matches = (m, filt = {}) => {
    const statusSel = filt.statusSel || {};
    const eff = knownStatus(m.status);
    if (
      (eff === "Prospect" || eff === "Terminated" || eff === "Archived") &&
      !statusSel[eff]
    )
      return false;

    const preds = [];
    if (filt.useDocs && hasDocTypes) preds.push(docMissing(m));
    if (filt.useDocsInOrder && hasDocTypes) preds.push(docsInOrder(m));
    if (filt.usePay && hasRounds) preds.push(paymentDue(m));
    if (filt.useStatus) preds.push(eff === null ? true : !!statusSel[eff]);

    if (preds.length === 0) return true;
    return filt.combine === "AND" ? preds.every(Boolean) : preds.some(Boolean);
  };

  const resolve = (members, filt) =>
    (members || []).filter((m) => matches(m, filt));

  return {
    hasDocTypes,
    hasRounds,
    receivedCount,
    docMissing,
    docsInOrder,
    paymentDue,
    amountDue,
    matches,
    resolve,
  };
}

// Default filter state for a fresh segment UI (status-only). Terminated and
// Archived off; Prospect ON even though it now sits behind the same hard
// guard (141 correction): writing to prospects is how they convert, so a
// fresh segment's audience includes them — the ticked default is exactly
// what keeps the guard from biting here. It bites for segments saved before
// the vocabulary existed, whose statusSel carries no Prospect key.
export function defaultSegmentFilter() {
  return {
    combine: "AND",
    useDocs: false,
    useDocsInOrder: false,
    usePay: false,
    useStatus: true,
    statusSel: {
      Prospect: true,
      Active: true,
      Suspended: true,
      Terminated: false,
      Archived: false,
    },
  };
}

// Merge-field rendering (fired modes ii/iii) lives server-side as the single
// authority — see server/src/lib/bulkMailMerge.js. The frontend modal asks the
// server's /preview endpoint for rendered samples + the missing-merge skip
// list, so there is exactly one renderer. The token list it accepts:
//   {firstName} {lastName} {fullName} {companyName} {title} {email}
//   {ref} {caseTitle} {amountDue}
// {amountDue} (Phase-25 RESHAPE) is the per-recipient outstanding balance,
// frozen at fire-time from evaluator.amountDue() — the SAME computation the
// payment-due segment axis uses. It always resolves (formats 0 as a value), so
// it never triggers the missing-merge skip.
export const MERGE_TOKEN_NAMES = [
  "firstName",
  "lastName",
  "fullName",
  "companyName",
  "title",
  "email",
  "ref",
  "caseTitle",
  "amountDue",
];

// 132 §8 — THE TOWN HALL'S FOUR, kept as their own list rather than appended.
// They are CASE-LEVEL, like {ref} and {caseTitle}: they resolve from the
// town-hall event and are identical for every recipient, so they render in
// EVERY mode — including the Bcc'd draft, which is the mode a broadcast to two
// hundred claimants actually wants. `MERGE_TOKEN_NAMES` above is the
// per-recipient list and the modal keeps advertising it in the merge mode
// alone; appending to it would have said these four need a personalised send.
//
// {eventJoin} is the Teams join link, the SAME one for everybody — and it is
// the same for everybody precisely because no claimant is an attendee: the
// meeting is a standalone `/me/onlineMeetings`, so there is no per-person
// invitation and therefore no roster to disclose. That is the fence brief 122
// and 123 both drew, and this is what lets a broadcast carry a meeting at all.
export const EVENT_MERGE_TOKEN_NAMES = [
  "eventDate",
  "eventTime",
  "eventPlace",
  "eventJoin",
];
